import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  useApiAdminwidgetsGetCollection,
  useApiWidgetWorkspacesGetCollection,
  apiAdminwidgetWorkspacesIdDelete,
  apiAdminwidgetWorkspacesIdPatch,
  getApiWidgetWorkspacesGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import { useQueryClient } from '@tanstack/react-query';
import type { ContentBlockPageId } from '@r/helpers/widget-workspace-slides.ts';
import { ContentBlockHubBoard } from '@admin/components/personalization/content-block-hub-board.tsx';
import {
  CONTENT_BLOCK_PAGES,
  filterBlocksForPage,
  filterWidgetsForPage
} from '@admin/pages/personalization/content-block-pages.ts';
import {
  type BlockOrderOverrides,
  buildSortOrderPatches,
  getOrderedBlockIdsFromServer,
  mergeBlockOrder,
  reorderList
} from '@admin/pages/personalization/content-block-hub-order.ts';
import { toast } from 'sonner';
import { useOrvalErrorToast, useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

/**
 * Hub admin des blocs de contenu : onglets Home/Boutique, liste ordonnée (DnD → PATCH sortOrder),
 * activation, suppression, navigation vers création ou édition.
 */
export function ContentBlockHubPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();
  const [selectedPageId, setSelectedPageId] = useState<ContentBlockPageId>('home');
  const [orderOverrides, setOrderOverrides] = useState<BlockOrderOverrides>({});
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [togglingBlockId, setTogglingBlockId] = useState<number | null>(null);
  const [activeOverrides, setActiveOverrides] = useState<Record<number, boolean>>({});

  const widgetsQuery = useApiAdminwidgetsGetCollection();
  useOrvalQueryErrorToast(widgetsQuery, t('admin.contentblock.error'));

  const widgetWorkspacesQuery = useApiWidgetWorkspacesGetCollection({
    query: {
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnWindowFocus: false
    }
  });

  const widgets = widgetsQuery.data;
  const widgetWorkspaces = widgetWorkspacesQuery.data;

  const blockCountByPage = useMemo(() => {
    const counts = {} as Record<ContentBlockPageId, number>;
    for (const page of CONTENT_BLOCK_PAGES) {
      counts[page.id] = filterBlocksForPage(widgetWorkspaces, page.id).length;
    }
    return counts;
  }, [widgetWorkspaces]);

  const pageBlocks = useMemo(
    () => filterBlocksForPage(widgetWorkspaces, selectedPageId),
    [widgetWorkspaces, selectedPageId]
  );

  const serverBlockIds = useMemo(() => getOrderedBlockIdsFromServer(pageBlocks), [pageBlocks]);

  const orderedBlockIds = useMemo(
    () => mergeBlockOrder(orderOverrides[selectedPageId], serverBlockIds),
    [orderOverrides, selectedPageId, serverBlockIds]
  );

  const orderedBlocks = useMemo(() => {
    const blockById = new Map(pageBlocks.map((block) => [block.id, block]));
    return orderedBlockIds
      .map((id) => blockById.get(id))
      .filter((block): block is NonNullable<typeof block> => block !== undefined)
      .map((block) => ({
        ...block,
        isActive: activeOverrides[block.id ?? 0] ?? block.isActive
      }));
  }, [activeOverrides, orderedBlockIds, pageBlocks]);

  const pageWidgets = useMemo(
    () => filterWidgetsForPage(widgets, selectedPageId),
    [widgets, selectedPageId]
  );

  const usedWidgetIds = useMemo(
    () => new Set(pageBlocks.map((block) => block.widget.id)),
    [pageBlocks]
  );

  /** Widget template pour POST : priorité à un type pas encore instancié sur la page. */
  const widgetForNewBlock = useMemo(() => {
    if (pageWidgets.length === 0) {
      return undefined;
    }

    const unusedWidget = pageWidgets.find((widget) => !usedWidgetIds.has(widget.id));
    return unusedWidget ?? pageWidgets[0];
  }, [pageWidgets, usedWidgetIds]);

  const canAddBlock = pageWidgets.length > 0;

  useEffect(() => {
    setActiveOverrides({});
  }, [widgetWorkspaces]);

  /** Drag-and-drop : met à jour l’ordre local puis PATCH `sortOrder` sur chaque bloc déplacé. */
  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (isSavingOrder) return;

      const currentOrder = mergeBlockOrder(orderOverrides[selectedPageId], serverBlockIds);
      const nextOrder = reorderList(currentOrder, fromIndex, toIndex);
      if (nextOrder.join(',') === currentOrder.join(',')) {
        return;
      }

      const blockById = new Map<number, (typeof pageBlocks)[number]>();
      for (const block of pageBlocks) {
        if (typeof block.id === 'number') {
          blockById.set(block.id, block);
        }
      }
      const patches = buildSortOrderPatches(nextOrder, blockById);
      const previousPageOrder = orderOverrides[selectedPageId];

      setOrderOverrides((previous) => ({ ...previous, [selectedPageId]: nextOrder }));

      if (patches.length === 0) {
        return;
      }

      setIsSavingOrder(true);

      void (async () => {
        try {
          await Promise.all(
            patches.map(({ id, sortOrder }) =>
              apiAdminwidgetWorkspacesIdPatch(String(id), { sortOrder })
            )
          );

          await queryClient.invalidateQueries({
            queryKey: getApiWidgetWorkspacesGetCollectionQueryKey()
          });

          setOrderOverrides((previous) => {
            const next = { ...previous };
            delete next[selectedPageId];
            return next;
          });

          toast.success(t('admin.contentblock.hub.order.saved'));
        } catch (error) {
          setOrderOverrides((previous) => {
            const next = { ...previous };
            if (previousPageOrder) {
              next[selectedPageId] = previousPageOrder;
            } else {
              delete next[selectedPageId];
            }
            return next;
          });
          showOrvalErrorToast(error, t('admin.contentblock.hub.order.error'));
        } finally {
          setIsSavingOrder(false);
        }
      })();
    },
    [
      isSavingOrder,
      orderOverrides,
      pageBlocks,
      queryClient,
      selectedPageId,
      serverBlockIds,
      showOrvalErrorToast,
      t
    ]
  );

  /** Ouvre l’éditeur d’une instance existante (`widget_workspace.id`). */
  const handleSelectBlock = (workspaceId: number) => {
    void navigate({
      to: '/content/content-block/$workspaceId',
      params: { workspaceId: String(workspaceId) }
    });
  };

  /** Création : route `/new` avec page cible + id du modèle widget (pas d’appel API avant l’éditeur). */
  const handleAddBlock = () => {
    if (!widgetForNewBlock?.id) return;

    void navigate({
      to: '/content/content-block/new',
      search: { pageId: selectedPageId, widgetId: widgetForNewBlock.id }
    });
  };

  const handleDeleteBlock = (workspaceId: number, label: string) => {
    toast(t('admin.contentblock.confirm.deletewidget', { label }), {
      action: {
        label: t('admin.contentblock.confirm.yes'),
        onClick: async () => {
          try {
            await apiAdminwidgetWorkspacesIdDelete(String(workspaceId));
            toast.success(t('admin.contentblock.delete.success'));

            setOrderOverrides((previous) => {
              const next: BlockOrderOverrides = { ...previous };
              for (const page of CONTENT_BLOCK_PAGES) {
                const order = next[page.id];
                if (!order) continue;
                next[page.id] = order.filter((id) => id !== workspaceId);
              }
              return next;
            });

            await queryClient.invalidateQueries({
              queryKey: getApiWidgetWorkspacesGetCollectionQueryKey()
            });
          } catch (error) {
            showOrvalErrorToast(error, t('admin.contentblock.delete.error'));
          }
        }
      },
      cancel: {
        label: t('admin.contentblock.confirm.no'),
        onClick: () => {}
      }
    });
  };

  const handleToggleActive = async (workspaceId: number, isActive: boolean) => {
    setTogglingBlockId(workspaceId);
    setActiveOverrides((previous) => ({ ...previous, [workspaceId]: isActive }));

    try {
      await apiAdminwidgetWorkspacesIdPatch(String(workspaceId), { isActive });
      await queryClient.invalidateQueries({
        queryKey: getApiWidgetWorkspacesGetCollectionQueryKey()
      });
    } catch (error) {
      setActiveOverrides((previous) => {
        const next = { ...previous };
        delete next[workspaceId];
        return next;
      });
      showOrvalErrorToast(error, t('admin.contentblock.hub.toggle.error'));
    } finally {
      setTogglingBlockId(null);
    }
  };

  if (widgetsQuery.isLoading || widgetWorkspacesQuery.isLoading) {
    return <div className="p-4">{t('admin.contentblock.loading')}</div>;
  }

  if (widgetsQuery.isError || widgetWorkspacesQuery.isError) {
    return <div className="p-4">{t('admin.contentblock.error')}</div>;
  }

  return (
    <ContentBlockHubBoard
      selectedPageId={selectedPageId}
      onSelectPage={setSelectedPageId}
      blockCountByPage={blockCountByPage}
      orderedBlocks={orderedBlocks}
      canAddBlock={canAddBlock}
      isSavingOrder={isSavingOrder}
      togglingBlockId={togglingBlockId}
      onReorder={handleReorder}
      onSelectBlock={handleSelectBlock}
      onAddBlock={handleAddBlock}
      onDeleteBlock={handleDeleteBlock}
      onToggleActive={(workspaceId, isActive) => {
        void handleToggleActive(workspaceId, isActive);
      }}
    />
  );
}
